import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, RefreshCw, AlertCircle, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronUp, ExternalLink, RotateCcw, Clock,
  Link2, Search, BookOpen, X, Save,
} from 'lucide-react';
import apiClient from '../../services/api';

const NAVY = '#1e3a5f';
const PHASE_LABELS = {
  A: 'Core Sales Enablement', B: 'Operational Process Guides',
  C: 'Customer-Facing Marketing', D: 'Email Template Library',
  E: 'Industry Depth & RFP Toolkit',
};
const PHASE_COLORS = {
  A: '#16a34a', B: '#0891b2', C: '#7c3aed', D: '#ea580c', E: '#d97706',
};
const CAT_COLORS = {
  'Sales Enablement': '#16a34a', 'Process Guides': '#0ea5e9',
  'How-To Guides': '#8b5cf6', 'Email Templates': '#ea580c',
  'Marketing': '#d97706', 'Internal Guides': '#64748b',
};
const STATUS_META = {
  published: { label: 'Published', bg: '#dcfce7', color: '#166534' },
  draft:     { label: 'Draft',     bg: '#fef9c3', color: '#854d0e' },
  archived:  { label: 'Archived',  bg: '#f1f5f9', color: '#64748b' },
  deleted:   { label: 'Deleted',   bg: '#fee2e2', color: '#991b1b' },
};

function VersionBadge({ version, status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
      style={{ background: meta.bg, color: meta.color }}>
      v{version} {meta.label}
    </span>
  );
}

function ChangeIndicator({ changesDetected, promptChanged, briefChanged, hasVersions }) {
  if (!hasVersions) return null;
  if (!changesDetected) return (
    <span className="flex items-center gap-1 text-xs" style={{ color: '#16a34a' }}>
      <CheckCircle2 size={11} /> Up to date
    </span>
  );
  const what = [promptChanged && 'prompt', briefChanged && 'Master Brief'].filter(Boolean).join(' + ');
  return (
    <span className="flex items-center gap-1 text-xs" style={{ color: '#d97706' }}>
      <AlertTriangle size={11} /> {what} updated
    </span>
  );
}

function VersionHistory({ versions, onView }) {
  return (
    <div className="space-y-1.5 pt-2" style={{ borderTop: '1px solid #f1f5f9' }}>
      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#94a3b8' }}>
        Version history
      </p>
      {versions.map((rec) => {
        const meta = rec.exists
          ? (STATUS_META[rec.current_status] || STATUS_META.draft)
          : STATUS_META.deleted;
        const fmtDate = rec.created_at
          ? new Date(rec.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
          : '';
        return (
          <div key={rec.resource_id}
            className="flex items-center justify-between gap-2 text-xs rounded-lg px-2.5 py-1.5"
            style={{ background: '#f8fafc' }}>
            <div className="flex items-center gap-2">
              <span className="font-bold" style={{ color: NAVY }}>v{rec.version}</span>
              <span className="px-1.5 py-0.5 rounded-full font-semibold text-xs"
                style={{ background: meta.bg, color: meta.color }}>
                {rec.exists ? rec.current_status : 'deleted'}
              </span>
              {fmtDate && (
                <span className="flex items-center gap-1" style={{ color: '#94a3b8' }}>
                  <Clock size={10} /> {fmtDate}
                </span>
              )}
            </div>
            {rec.exists && (
              <button
                onClick={() => onView(rec.current_slug || rec.resource_slug)}
                className="flex items-center gap-1 font-semibold hover:underline"
                style={{ color: '#0891b2' }}>
                Open <ExternalLink size={10} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Adopt existing resource modal ─────────────────────────────────────────────
function AdoptModal({ prompt, onConfirm, onCancel }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [adopting, setAdopting]   = useState(false);

  useEffect(() => {
    apiClient.get('/admin/resource-generator/adoptable-resources')
      .then(r => setResources(r.data || []))
      .catch(() => toast.error('Failed to load resources'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = resources.filter(r =>
    !search ||
    r.title.toLowerCase().includes(search.toLowerCase()) ||
    r.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirm = async () => {
    if (!selected) return;
    setAdopting(true);
    try {
      await onConfirm(selected);
    } finally {
      setAdopting(false);
    }
  };

  const nextVersion = (prompt.version_status?.total_versions || 0) + 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-6 pb-4" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#eff6ff' }}>
              <Link2 size={18} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <h3 className="font-bold text-base" style={{ color: '#1e3a5f' }}>
                Link existing resource
              </h3>
              <p className="text-sm mt-0.5" style={{ color: '#64748b' }}>
                Adopt an existing resource as <strong>v{nextVersion}</strong> of<br/>
                <span className="font-medium" style={{ color: '#1e3a5f' }}>{prompt.title}</span>
              </p>
            </div>
          </div>
          <div className="relative mt-4">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
            <Input
              placeholder="Search resources…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Resource list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && <p className="text-sm text-center py-4" style={{ color: '#94a3b8' }}>Loading…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-center py-4" style={{ color: '#94a3b8' }}>No resources found</p>
          )}
          {filtered.map(r => {
            const isAlreadyLinked = !!r.generated_from_prompt;
            const isSelected = selected?.id === r.id;
            return (
              <button
                key={r.id}
                onClick={() => !isAlreadyLinked && setSelected(r)}
                disabled={isAlreadyLinked}
                className="w-full text-left rounded-xl p-3 flex items-start gap-3 border transition-all"
                style={{
                  background: isSelected ? '#eff6ff' : isAlreadyLinked ? '#f8fafc' : 'white',
                  borderColor: isSelected ? '#2563eb' : '#e2e8f0',
                  opacity: isAlreadyLinked ? 0.5 : 1,
                  cursor: isAlreadyLinked ? 'not-allowed' : 'pointer',
                }}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: '#1e3a5f' }}>{r.title}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>/{r.slug}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.status && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{
                        background: r.status === 'published' ? '#dcfce7' : '#fef9c3',
                        color:      r.status === 'published' ? '#166534' : '#854d0e',
                      }}>
                      {r.status}
                    </span>
                  )}
                  {isAlreadyLinked && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: '#f1f5f9', color: '#94a3b8' }}>
                      linked
                    </span>
                  )}
                  {isSelected && <CheckCircle2 size={16} style={{ color: '#2563eb' }} />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 flex gap-2" style={{ borderTop: '1px solid #f1f5f9' }}>
          <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!selected || adopting}
            className="flex-1 gap-2"
            style={{ background: '#1e3a5f', color: 'white' }}>
            {adopting ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
            Adopt as v{nextVersion}
          </Button>
        </div>
      </div>
    </div>
  );
}


function RegenerateModal({ prompt, onConfirm, onCancel }) {
  const vs = prompt.version_status;
  const nextVersion = (vs?.total_versions || 0) + 1;
  const latestStatus = vs?.latest?.current_status;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: '#fef3c7' }}>
            <RotateCcw size={18} style={{ color: '#d97706' }} />
          </div>
          <div>
            <h3 className="font-bold text-base" style={{ color: NAVY }}>
              Regenerate Resource?
            </h3>
            <p className="text-sm mt-0.5" style={{ color: '#64748b' }}>
              {prompt.title}
            </p>
          </div>
        </div>

        <div className="rounded-xl p-4 space-y-2 text-sm"
          style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <p style={{ color: '#334155' }}>
            This will create <strong>v{nextVersion} (Draft)</strong> as a brand-new resource.
          </p>
          {latestStatus === 'published' && (
            <p style={{ color: '#334155' }}>
              Your current <strong>v{vs.total_versions} (Published)</strong> resource will
              {' '}<strong>not be touched</strong> - it stays live until you choose to replace it.
            </p>
          )}
          {latestStatus === 'draft' && (
            <p style={{ color: '#334155' }}>
              Your existing <strong>v{vs.total_versions} (Draft)</strong> will remain
              alongside the new draft. You can delete it once you are satisfied with the new version.
            </p>
          )}
        </div>

        {vs?.changes_detected ? (
          <div className="rounded-xl p-3 flex items-start gap-2"
            style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
            <AlertTriangle size={14} style={{ color: '#d97706', marginTop: 1, flexShrink: 0 }} />
            <p className="text-xs" style={{ color: '#92400e' }}>
              <strong>Content update available.</strong>{' '}
              {[vs.prompt_changed && 'The prompt template', vs.brief_changed && 'the Master Brief']
                .filter(Boolean).join(' and ')}{' '}
              {(vs.prompt_changed && vs.brief_changed) ? 'have' : 'has'} changed since v{vs.total_versions} was generated.
              The new version will reflect these updates.
            </p>
          </div>
        ) : (
          <div className="rounded-xl p-3 flex items-start gap-2"
            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <CheckCircle2 size={14} style={{ color: '#16a34a', marginTop: 1, flexShrink: 0 }} />
            <p className="text-xs" style={{ color: '#166534' }}>
              <strong>No changes detected.</strong> The prompt and Master Brief are unchanged since the last generation.
              The new version will be similar to v{vs.total_versions} but may vary slightly due to AI non-determinism.
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
          <Button onClick={onConfirm} className="flex-1 gap-2"
            style={{ background: NAVY, color: 'white' }}>
            <RotateCcw size={13} /> Generate v{nextVersion}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Prompt card ───────────────────────────────────────────────────────────────
function PromptCard({ prompt, onGenerate, onRegenerate, onAdopt, generating }) {
  const [showHistory, setShowHistory] = useState(false);
  const navigate = useNavigate();
  const catColor = CAT_COLORS[prompt.category] || '#64748b';
  const isGenerating = generating === prompt.prompt_id;
  const vs = prompt.version_status;
  const hasVersions = vs?.total_versions > 0;
  const latest = vs?.latest;
  const allVersions = vs?.all_versions || [];
  const isAdopted = latest?.adopted;

  const handleView = (slug) => navigate(`/admin/resources/${slug}/edit`);

  return (
    <div className="rounded-xl border bg-white p-5 flex flex-col gap-3 transition-shadow hover:shadow-md">

      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-snug" style={{ color: NAVY }}>{prompt.title}</p>
          <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>{prompt.audience}</p>
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5"
          style={{ background: catColor + '15', color: catColor }}>
          {prompt.category}
        </span>
      </div>

      {/* Version status row */}
      {hasVersions && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <VersionBadge version={latest.version} status={latest.current_status} />
            {isAdopted && (
              <span className="flex items-center gap-1 text-xs"
                style={{ color: '#0891b2' }}>
                <Link2 size={10} /> Adopted
              </span>
            )}
            <ChangeIndicator
              changesDetected={vs.changes_detected}
              promptChanged={vs.prompt_changed}
              briefChanged={vs.brief_changed}
              hasVersions={hasVersions}
            />
          </div>
          {allVersions.length > 1 && (
            <button
              onClick={() => setShowHistory(h => !h)}
              className="flex items-center gap-0.5 text-xs"
              style={{ color: '#0891b2' }}>
              {allVersions.length} versions
              {showHistory ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          )}
        </div>
      )}

      {/* Version history */}
      {showHistory && allVersions.length > 0 && (
        <VersionHistory versions={allVersions} onView={handleView} />
      )}

      {/* Action buttons */}
      <div className="flex gap-2 mt-auto flex-wrap">
        {hasVersions && latest?.exists && (
          <Button variant="outline" size="sm" className="flex-1 gap-1.5"
            onClick={() => handleView(latest.current_slug || latest.resource_slug)}>
            <ExternalLink size={12} /> Open v{latest.version}
          </Button>
        )}

        {!hasVersions ? (
          <div className="flex gap-2 w-full">
            <Button
              size="sm"
              onClick={() => onGenerate(prompt)}
              disabled={!!generating}
              className="flex-1 gap-2"
              style={{ background: isGenerating ? '#94a3b8' : NAVY, color: 'white' }}
              data-testid={`generate-btn-${prompt.prompt_id}`}>
              {isGenerating
                ? <><Loader2 size={13} className="animate-spin" /> Generating…</>
                : <><Sparkles size={13} /> Generate</>
              }
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAdopt(prompt)}
              disabled={!!generating}
              className="gap-1.5 px-3"
              title="Link an existing resource as v1 of this prompt"
              data-testid={`adopt-btn-${prompt.prompt_id}`}>
              <Link2 size={12} /> Link
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={() => onRegenerate(prompt)}
            disabled={!!generating}
            className={`gap-1.5 ${hasVersions && latest?.exists ? 'flex-1' : 'w-full'}`}
            style={{
              background: isGenerating ? '#94a3b8' : (vs.changes_detected ? '#d97706' : '#64748b'),
              color: 'white',
            }}
            data-testid={`regenerate-btn-${prompt.prompt_id}`}>
            {isGenerating
              ? <><Loader2 size={12} className="animate-spin" /> Generating…</>
              : <><RotateCcw size={12} /> Regenerate</>
            }
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ResourceGeneratorPage() {
  const navigate = useNavigate();
  const [data, setData]             = useState(null);   // { prompts, master_brief_hash, ... }
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(null);   // prompt_id being generated
  const [regenTarget, setRegenTarget] = useState(null); // prompt to confirm regen for
  const [adoptTarget, setAdoptTarget] = useState(null); // prompt to adopt a resource into
  const [seeding, setSeeding]       = useState(false);

  // Master Brief modal
  const [showBrief, setShowBrief]   = useState(false);
  const [briefText, setBriefText]   = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefSaving, setBriefSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/resource-generator/status');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await apiClient.post('/admin/resource-generator/seed');
      toast.success(res.data.message || 'Prompts loaded');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const handleOpenBrief = async () => {
    setShowBrief(true);
    setBriefLoading(true);
    try {
      const res = await apiClient.get('/admin/resource-generator/master-brief');
      setBriefText(res.data.prompt_text || '');
    } catch (e) {
      toast.error('Failed to load Master Brief');
      setShowBrief(false);
    } finally {
      setBriefLoading(false);
    }
  };

  const handleSaveBrief = async () => {
    if (!briefText.trim()) return;
    setBriefSaving(true);
    try {
      await apiClient.put('/admin/resource-generator/master-brief', { prompt_text: briefText });
      toast.success('Master Brief saved - all prompt cards will show Update available');
      setShowBrief(false);
      await load(); // refresh change-detection hashes
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBriefSaving(false);
    }
  };

  const doGenerate = async (prompt) => {
    setGenerating(prompt.prompt_id);
    setRegenTarget(null);
    try {
      const vs = prompt.version_status;
      const nextVersion = (vs?.total_versions || 0) + 1;

      // Step 1: Start the job - returns immediately with job_id
      const startRes = await apiClient.post('/admin/resource-generator/generate', {
        prompt_id: prompt.prompt_id,
      });
      const { job_id } = startRes.data;

      // Step 2: Poll every 2 seconds until complete or failed (max 3 min)
      const deadline = Date.now() + 3 * 60 * 1000;
      let result = null;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        const pollRes = await apiClient.get(`/admin/resource-generator/jobs/${job_id}`);
        const job = pollRes.data;
        if (job.status === 'complete') { result = job.result; break; }
        if (job.status === 'failed')   { throw new Error(job.error || 'Generation failed'); }
      }
      if (!result) throw new Error('Generation timed out - please try again');

      toast.success(`v${result.version} Draft created - opening editor`, { duration: 3000 });
      navigate(`/admin/resources/${result.slug || result.resource_id}/edit`);
      await load(); // refresh status on return
    } catch (e) {
      toast.error(e.message || e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(null);
    }
  };

  const handleGenerate   = (prompt) => doGenerate(prompt);
  const handleRegenerate = (prompt) => setRegenTarget(prompt);
  const handleAdopt      = (prompt) => setAdoptTarget(prompt);
  const confirmRegenerate = () => { if (regenTarget) doGenerate(regenTarget); };

  const confirmAdopt = async (resource) => {
    try {
      const res = await apiClient.post('/admin/resource-generator/adopt', {
        prompt_id:   adoptTarget.prompt_id,
        resource_id: resource.id,
      });
      toast.success(res.data.message);
      setAdoptTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Adoption failed');
    }
  };

  const prompts = data?.prompts || [];
  const isEmpty = !loading && prompts.length === 0;

  // Group by phase
  const grouped = prompts.reduce((acc, p) => {
    const phase = p.phase || 'OTHER';
    if (!acc[phase]) acc[phase] = [];
    acc[phase].push(p);
    return acc;
  }, {});

  // Summary stats
  const totalGenerated = prompts.filter(p => p.version_status?.total_versions > 0).length;
  const totalUpdates   = prompts.filter(p => p.version_status?.changes_detected).length;

  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
            Resource Generator
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            AI-powered resource creation with version tracking and change detection.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <div className="flex items-center gap-3 text-xs" style={{ color: '#64748b' }}>
              <span><strong style={{ color: NAVY }}>{totalGenerated}</strong> / {prompts.length} generated</span>
              {totalUpdates > 0 && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full font-semibold"
                  style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>
                  <AlertTriangle size={11} /> {totalUpdates} update{totalUpdates !== 1 ? 's' : ''} available
                </span>
              )}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={handleOpenBrief} className="gap-1.5" data-testid="btn-view-master-brief">
            <BookOpen size={13} />
            Master Brief
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-xl p-4 flex items-start gap-3"
        style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <Sparkles size={16} style={{ color: '#2563eb', marginTop: 2, flexShrink: 0 }} />
        <div className="text-sm space-y-1" style={{ color: '#1d4ed8' }}>
          <p><strong>How it works:</strong> Select a prompt and click Generate. Claude reads the Master Brief + prompt and builds all content blocks as a draft resource ready to review.</p>
          <p className="text-xs" style={{ color: '#3b82f6' }}>
            <strong>Version tracking:</strong> Each generation creates a new version. Existing published resources are never modified.
            {' '}<strong>Change detection:</strong> Amber "Regenerate" = prompt or Master Brief has changed since last generation.
            {' '}Grey "Regenerate" = no changes detected.
          </p>
        </div>
      </div>

      {/* Empty/seed state */}
      {isEmpty && (
        <div className="rounded-2xl border border-dashed bg-white p-12 text-center">
          <AlertCircle size={28} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold mb-1" style={{ color: NAVY }}>Prompt library not loaded</p>
          <p className="text-sm mb-4" style={{ color: 'hsl(var(--muted-fg))' }}>
            Load the 25 prompt templates to get started.
          </p>
          <Button onClick={handleSeed} disabled={seeding} style={{ background: NAVY, color: 'white' }} className="gap-2">
            {seeding ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Load Prompts
          </Button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      )}

      {/* Generating overlay */}
      {generating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(15,23,42,0.6)' }}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: '#eff6ff' }}>
              <Sparkles size={24} style={{ color: '#2563eb' }} className="animate-pulse" />
            </div>
            <p className="font-bold text-lg mb-1" style={{ color: NAVY }}>Generating with Claude…</p>
            <p className="text-sm" style={{ color: '#64748b' }}>
              Building all content blocks - decision trees, tables, email templates, scenario cards.<br />
              This takes 15–30 seconds.
            </p>
            <div className="mt-4 flex justify-center gap-1.5">
              {[0,1,2].map(i => (
                <span key={i} className="w-2 h-2 rounded-full bg-blue-500 animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Regen confirmation modal */}
      {regenTarget && (
        <RegenerateModal
          prompt={regenTarget}
          onConfirm={confirmRegenerate}
          onCancel={() => setRegenTarget(null)}
        />
      )}

      {/* Adopt existing resource modal */}
      {adoptTarget && (
        <AdoptModal
          prompt={adoptTarget}
          onConfirm={confirmAdopt}
          onCancel={() => setAdoptTarget(null)}
        />
      )}

      {/* Prompt groups */}
      {!loading && !isEmpty && Object.entries(grouped).sort().map(([phase, items]) => (
        <div key={phase}>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
              style={{ background: PHASE_COLORS[phase] || '#64748b' }}>
              {phase}
            </span>
            <h2 className="font-bold text-sm" style={{ color: NAVY }}>
              {PHASE_LABELS[phase] || phase}
            </h2>
            <span className="text-xs" style={{ color: '#94a3b8' }}>{items.length} prompts</span>
            <span className="text-xs" style={{ color: '#94a3b8' }}>
              · {items.filter(p => p.version_status?.total_versions > 0).length} generated
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(p => (
              <PromptCard
                key={p.prompt_id}
                prompt={p}
                onGenerate={handleGenerate}
                onRegenerate={handleRegenerate}
                onAdopt={handleAdopt}
                generating={generating}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Footer note */}
      {!loading && !isEmpty && (
        <p className="text-xs text-center pb-4" style={{ color: 'hsl(var(--muted-fg))' }}>
          Generated resources are saved as <strong>Draft</strong>.
          Review in the Resource Editor before publishing.
          The Master Brief can be updated to reflect C4P or Ascarii website changes -
          all prompt cards will then show <span style={{ color: '#d97706', fontWeight: 600 }}>amber Regenerate</span> to flag available updates.
        </p>
      )}

      {/* ── Master Brief Modal ─────────────────────────────────────────── */}
      {showBrief && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }}>
          <div
            data-testid="master-brief-modal"
            className="relative w-full max-w-3xl flex flex-col rounded-xl shadow-2xl"
            style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', maxHeight: '90vh' }}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
              <div className="flex items-center gap-2">
                <BookOpen size={16} style={{ color: NAVY }} />
                <span className="font-semibold text-sm" style={{ color: NAVY }}>Master Brief</span>
              </div>
              <button
                onClick={() => setShowBrief(false)}
                className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                data-testid="btn-close-master-brief"
              >
                <X size={15} style={{ color: 'hsl(var(--muted-foreground))' }} />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {briefLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-4/6" />
                </div>
              ) : (
                <>
                  <p className="text-xs mb-3" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    The Master Brief is the shared context Claude reads before generating any resource. Edit it here to update the factual background about C4P products, services, and Ascarii.
                    <strong className="ml-1">Saving will flag all 26 prompt cards as needing regeneration.</strong>
                  </p>
                  <textarea
                    data-testid="master-brief-textarea"
                    value={briefText}
                    onChange={e => setBriefText(e.target.value)}
                    rows={22}
                    className="w-full px-3 py-2.5 rounded-lg text-xs font-mono resize-none leading-relaxed"
                    style={{
                      background: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      color: 'hsl(var(--foreground))',
                    }}
                  />
                  <p className="text-xs mt-1.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {briefText.length.toLocaleString()} characters
                  </p>
                </>
              )}
            </div>

            {/* Modal footer */}
            {!briefLoading && (
              <div className="flex items-center justify-end gap-2 px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid hsl(var(--border))' }}>
                <Button variant="outline" size="sm" onClick={() => setShowBrief(false)}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={handleSaveBrief}
                  disabled={briefSaving || !briefText.trim()}
                  data-testid="btn-save-master-brief"
                  className="gap-1.5"
                  style={{ background: NAVY, color: 'white' }}
                >
                  {briefSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  {briefSaving ? 'Saving...' : 'Save Master Brief'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
